import { Options, request } from './request'

export interface IGetRecentlyUploadedNodesSchema {
  embedded?: boolean
  cursor_next?: string
  limit?: number
  total_count?: boolean
  preview?: boolean
  preview_jwt?:boolean
  file_info__created__gte?: string // format 2025-06-05T07:33:21.747000+00:00
  file_info__created__lte?: string
  parent_id?: string
}

interface IRecentlyUploadedResponse {
  _links: {
    self: { href: string }
    next?: { href: string }
  }
  _embedded: {
    nodes: Array<{
      id: string
      name: string
      type: string
      created: string
      updated: string
      description: string
      parent_id: string
      is_trashed: boolean
      is_explicitly_trashed: boolean
      is_shared: boolean
      author_id: number
      last_editor_id: number
      file_info: {
        id: string
        size: number
        checksum: string
        type: string
        created: string
        mime: string
      }
      author_name: string
      last_editor_name: string
      _links: {
        self: { href: string }
        set_share: { href: string }
        content: { href: string }
        children: { href: string }
      }
    }>
  }
}

class RecentlyUploadedIterator {
  private currentParams: IGetRecentlyUploadedNodesSchema
  private currentNodes: IRecentlyUploadedResponse['_embedded']['nodes'] = []
  private hasMore = true
  private nextUrl = ''
  private initialUrl = ''

  constructor(
    private userID: number,
    params: Omit<IGetRecentlyUploadedNodesSchema, 'cursor_next'>,
    private signal?: AbortSignal
  ) {
    this.currentParams = { ...params, cursor_next: '' } as IGetRecentlyUploadedNodesSchema
    this.initialUrl = `/api/3/users/${this.userID}/fs/root/recently_uploaded`
  }

  private async fetchNextPage() {

    const options: Options = {}

    if(this.signal) {
      options.signal = this.signal
    }
    const response = await request(
      'GET',
      this.nextUrl || this.initialUrl,
      this.currentParams,
      options
    ) as IRecentlyUploadedResponse

    this.initialUrl = ''

    this.currentNodes = response._embedded.nodes
    const nextUrl = response?._links?.next?.href
    this.hasMore = !!nextUrl

    if(this.hasMore) {
      const nextUrlObj = new URL(nextUrl)
      // get pathname and search only because the host is in the request method
      const { pathname, search } = nextUrlObj
      // clear params because params are in new next url
      this.currentParams = {}
      this.nextUrl = `${pathname}${search}`
    }
  }

  [Symbol.asyncIterator]() {
    return {
      next: async () => {
        if (!this.hasMore) {
          return { done: true, value: this.currentNodes }
        }

        await this.fetchNextPage()

        return {
          done: false,
          value: this.currentNodes
        }
      }
    }
  }
}

export const recentlyUploaded = {
  getRecentlyUploaded: (userID: number, params: IGetRecentlyUploadedNodesSchema, signal?: AbortSignal) => {
    return request('GET', `/api/3/users/${userID}/fs/root/recently_uploaded`, params, { signal })
  },

  getAllRecentlyUploaded: async (userID: number, params: IGetRecentlyUploadedNodesSchema, signal?: AbortSignal) => {
    const allNodes = []
    const iterator = new RecentlyUploadedIterator(userID, params, signal)
    for await (const node of iterator) {
      allNodes.push(node)
    }
    return allNodes
  },

  getAllRecentlyUploadedIterator(
    userID: number,
    params: Omit<IGetRecentlyUploadedNodesSchema, 'cursor_next'>,
    signal?: AbortSignal
  ) {
    return new RecentlyUploadedIterator(userID, params, signal)
  }
}


